import { Topic } from '@/core/notifications/model';
import { fieldHeaderLabel, fieldLeafKeys, leafLabel } from './tag-schema';

function toValueSet(value: unknown): Set<string> {
  if (value === undefined || value === null) return new Set();
  return new Set(Array.isArray(value) ? value.map(String) : [String(value)]);
}

// A field whose selected values equal its entire allowed universe is functionally
// a wildcard today (there's nothing else it could exclude) — treat it as unset so
// coverage/gap checks don't flag it as restrictive. Fields with no bounded `allowed`
// list (freeform) can't be measured this way and are left as-is.
function normalizeTagFilter(
  topic: Topic,
  tagFilter: Record<string, unknown>,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...tagFilter };
  Object.keys(tagFilter).forEach(field => {
    const universe = fieldLeafKeys(topic.tagSchema[field] ?? {});
    if (universe.length === 0) return;
    const selected = toValueSet(tagFilter[field]);
    if (universe.every(key => selected.has(key))) delete normalized[field];
  });
  return normalized;
}

// tagFilter values are AND-across-fields, any-of-within-a-field; an empty/missing
// field means "accept any value" (see subscription-form.tsx). Coverage compares
// leaf-key sets per field — values are always stored as flat leaf keys already.
export function subscriptionCoversFilter(
  topic: Topic | undefined,
  subTagFilter: Record<string, unknown>,
  viewTagFilter: Record<string, unknown>,
): boolean {
  const sub = topic ? normalizeTagFilter(topic, subTagFilter) : subTagFilter;
  const view = topic ? normalizeTagFilter(topic, viewTagFilter) : viewTagFilter;
  const fields = new Set([...Object.keys(sub), ...Object.keys(view)]);
  for (const field of fields) {
    const subValues = toValueSet(sub[field]);
    const viewValues = toValueSet(view[field]);
    if (subValues.size === 0) continue;
    if (viewValues.size === 0) return false;
    for (const v of viewValues) if (!subValues.has(v)) return false;
  }
  return true;
}

// Merges the current view's values into a subscription's restrictive fields (union).
// Wildcard fields (subscription already accepts anything) are left untouched, and
// fields the subscription restricts but the view doesn't mention are kept as-is.
export function mergeTagFilters(
  subTagFilter: Record<string, unknown>,
  viewTagFilter: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...subTagFilter };
  Object.keys(viewTagFilter).forEach(field => {
    const subValues = toValueSet(subTagFilter[field]);
    if (subValues.size === 0) return;
    const viewValues = toValueSet(viewTagFilter[field]);
    const union = [...new Set([...subValues, ...viewValues])];
    merged[field] = union.length === 1 ? union[0] : union;
  });
  return merged;
}

export interface CoverageGap {
  field: string;
  fieldName: string;
  missingValues: string[];
}

export function describeCoverageGap(
  topic: Topic,
  subTagFilter: Record<string, unknown>,
  viewTagFilter: Record<string, unknown>,
): CoverageGap[] {
  const sub = normalizeTagFilter(topic, subTagFilter);
  const view = normalizeTagFilter(topic, viewTagFilter);
  const gaps: CoverageGap[] = [];
  Object.keys(view).forEach(field => {
    const subValues = toValueSet(sub[field]);
    if (subValues.size === 0) return;
    const viewValues = toValueSet(view[field]);
    const missing = [...viewValues].filter(v => !subValues.has(v));
    if (missing.length === 0) return;
    const schemaField = topic.tagSchema[field];
    gaps.push({
      field,
      fieldName: fieldHeaderLabel(schemaField, field),
      missingValues: missing.map(v => leafLabel(schemaField, v)),
    });
  });
  return gaps;
}

export interface TagFilterSummary {
  field: string;
  fieldName: string;
  values: string[];
}

// Human-readable summary of a filter, one entry per restricted field
// (a field absent from tagFilter is a wildcard and is not included).
export function describeTagFilter(
  topic: Topic,
  tagFilter: Record<string, unknown>,
): TagFilterSummary[] {
  return Object.keys(tagFilter)
    .map(field => {
      const schemaField = topic.tagSchema[field];
      const values = [...toValueSet(tagFilter[field])];
      return {
        field,
        fieldName: fieldHeaderLabel(schemaField, field),
        values: values.map(v => leafLabel(schemaField, v)),
      };
    })
    .filter(entry => entry.values.length > 0);
}
